import { useMemo } from 'react'
import type { PropsWithChildren } from 'react'
import { useApplicationContext } from '@mysaiva/core/application'
import { ApiKeysClientContext, isMockApiKeysEnabled } from './apiKeysClientContext'
import { HttpApiKeysClient } from './apiKeysClient'
import { MockApiKeysClient } from './mockApiKeysClient'
import type { ApiKeysClient } from './apiKeys.types'

interface ApiKeysClientProviderProps {
  /** Test seam; when given, neither env nor app context is consulted. */
  client?: ApiKeysClient
}

/**
 * Chooses the API keys client for the selected organisation: the mock when
 * `VITE_DEVELOPER_PLATFORM_MOCK_API=true`, otherwise the provisional HTTP
 * client (SAIV-11350). Re-created when the organisation changes.
 */
export function ApiKeysClientProvider({
  client,
  children
}: PropsWithChildren<ApiKeysClientProviderProps>) {
  const { apiClient, orgContext } = useApplicationContext()
  const orgId = orgContext.org.id

  const resolved = useMemo<ApiKeysClient>(() => {
    if (client) return client
    if (isMockApiKeysEnabled()) return new MockApiKeysClient()
    return new HttpApiKeysClient(apiClient, orgId)
  }, [client, apiClient, orgId])

  return <ApiKeysClientContext.Provider value={resolved}>{children}</ApiKeysClientContext.Provider>
}
